/**
 * Notifications Feed Widget — Premium
 * Event-driven notifications with filters. UI/UX: hierarchy, spacing scale, depth, typography, micro-interactions.
 */

import {
  Box,
  Chip,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Typography,
  useTheme,
} from "@mui/material";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import FenceIcon from "@mui/icons-material/Fence";
import SensorsIcon from "@mui/icons-material/Sensors";
import PlaceIcon from "@mui/icons-material/Place";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { memo } from "react";
import { useNotifications } from "../../../../context/NotificationsContext";
import { TIME_WINDOWS } from "../../../../context/NotificationsContext";
import { unix_utc_to_local_12_hour_clock } from "../../../../utils/date_time_utils";

const EVENT_OPTIONS = [
  { value: "", label: "All events" },
  { value: "Zone Departure", label: "Zone Departure" },
  { value: "Zone Arrival", label: "Zone Arrival" },
  { value: "Leave Geofence", label: "Leave Geofence" },
  { value: "Enter Geofence", label: "Enter Geofence" },
  { value: "Origin", label: "Origin" },
  { value: "Receive", label: "Receive" },
  { value: "Moving", label: "Moving" },
  { value: "Alert", label: "Alert" },
  { value: "Warning", label: "Warning" },
];

const RADIUS = { card: 2, block: 2, icon: 2 };

const NotificationsFeed = memo(function NotificationsFeed({ timeZone = "UTC" }) {
  const theme = useTheme();
  const { palette = {} } = theme;
  const { primary = {}, success = {}, warning = {}, text = {}, divider, mode } = palette;
  const isDark = mode === "dark";
  const navyColor = primary?.main || "#002843";
  const greenColor = success?.main || "#A0C46D";
  const warningColor = warning?.main || "#f59e0b";
  const textPrimary = text?.primary || (isDark ? "#ffffff" : "#1e293b");
  const textSecondary = text?.secondary || (isDark ? "#94a3b8" : "#64748b");
  const borderColor = divider || (isDark ? "rgba(148, 163, 184, 0.18)" : "rgba(0, 40, 67, 0.08)");
  const subtleBg = isDark ? "rgba(255,255,255,0.03)" : "rgba(0, 40, 67, 0.02)";

  const {
    notifications,
    eventTypeFilter,
    setEventTypeFilter,
    timeWindowKey,
    setTimeWindowKey,
  } = useNotifications();

  const getEventIcon = (eventName) => {
    const e = (eventName || "").toLowerCase();
    if (e.includes("departure") || e.includes("leave") || e.includes("moving"))
      return <LocalShippingIcon sx={{ fontSize: "1rem" }} />;
    if (e.includes("arrival") || e.includes("enter") || e.includes("receive") || e.includes("origin"))
      return <WarehouseIcon sx={{ fontSize: "1rem" }} />;
    if (e.includes("geofence") || e.includes("zone")) return <FenceIcon sx={{ fontSize: "1rem" }} />;
    if (e.includes("scan") || e.includes("proximity")) return <SensorsIcon sx={{ fontSize: "1rem" }} />;
    if (e.includes("alert") || e.includes("warning")) return <WarningAmberIcon sx={{ fontSize: "1rem" }} />;
    return <PlaceIcon sx={{ fontSize: "1rem" }} />;
  };

  const getEventColor = (eventName, severity) => {
    if (severity === "warning") return warningColor;
    const e = (eventName || "").toLowerCase();
    if (e.includes("departure") || e.includes("leave")) return "#8b5cf6";
    if (e.includes("arrival") || e.includes("enter") || e.includes("receive")) return greenColor;
    if (e.includes("moving")) return "#06b6d4";
    return isDark ? "#94a3b8" : "#64748b";
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return unix_utc_to_local_12_hour_clock(timestamp, timeZone);
  };

  const displayList = notifications.slice(0, 12);
  const currentEventFilter = eventTypeFilter && eventTypeFilter[0] ? eventTypeFilter[0] : "";

  return (
    <Paper
      component="article"
      role="region"
      aria-label="Notifications feed"
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: RADIUS.card,
        border: `1px solid ${borderColor}`,
        background: isDark
          ? "linear-gradient(160deg, #042a3f 0%, #021e2d 50%, #011a26 100%)"
          : "linear-gradient(160deg, #ffffff 0%, #fafbfc 50%, #f8fafc 100%)",
        boxShadow: isDark ? "0 2px 12px rgba(0,0,0,0.2)" : "0 2px 8px rgba(0,0,0,0.06)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "3px",
          background: isDark
            ? `linear-gradient(90deg, ${greenColor} 0%, ${navyColor} 100%)`
            : `linear-gradient(90deg, ${navyColor} 0%, ${greenColor} 100%)`,
        },
      }}
    >
      {/* Header — premium hierarchy + badge */}
      <Box
        component="header"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 14px",
          borderBottom: `1px solid ${borderColor}`,
          flexShrink: 0,
          background: subtleBg,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box
            aria-hidden="true"
            sx={{
              width: 36,
              height: 36,
              borderRadius: RADIUS.icon,
              background: isDark
                ? `linear-gradient(145deg, ${navyColor}45 0%, ${navyColor}18 100%)`
                : `linear-gradient(145deg, ${navyColor}14 0%, ${navyColor}06 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `1px solid ${navyColor}28`,
            }}
          >
            <NotificationsActiveIcon sx={{ color: isDark ? greenColor : navyColor, fontSize: "1.125rem" }} />
          </Box>
          <Box>
            <Typography
              component="h2"
              sx={{
                fontWeight: 700,
                color: isDark ? greenColor : navyColor,
                fontSize: "1rem",
                lineHeight: 1.2,
                letterSpacing: "-0.01em",
              }}
            >
              Notifications
            </Typography>
            <Typography sx={{ color: textSecondary, fontSize: "0.6875rem", fontWeight: 500, letterSpacing: "0.02em" }}>
              Event-driven alerts
            </Typography>
          </Box>
        </Box>
        <Chip
          label={notifications.length}
          size="small"
          sx={{
            height: 22,
            minWidth: 22,
            fontSize: "0.6875rem",
            fontWeight: 700,
            fontVariantNumeric: "tabular-nums",
            backgroundColor: isDark ? "rgba(160, 196, 109, 0.2)" : "rgba(160, 196, 109, 0.14)",
            color: greenColor,
            border: `1px solid ${greenColor}40`,
            borderRadius: "2px",
            "& .MuiChip-label": { px: 1 },
          }}
        />
      </Box>

      {/* Filters — premium dropdowns */}
      <Box sx={{ display: "flex", gap: 1, padding: "8px 14px", borderBottom: `1px solid ${borderColor}`, flexShrink: 0 }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={currentEventFilter}
            onChange={(e) => setEventTypeFilter(e.target.value ? [e.target.value] : [])}
            displayEmpty
            sx={{
              fontSize: "0.75rem",
              fontWeight: 500,
              borderRadius: "2px",
              backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
              border: `1px solid ${borderColor}`,
              "& .MuiSelect-select": { py: 0.5 },
              "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)" },
            }}
          >
            {EVENT_OPTIONS.map((opt) => (
              <MenuItem key={opt.value || "all"} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <Select
            value={timeWindowKey}
            onChange={(e) => setTimeWindowKey(e.target.value)}
            sx={{
              fontSize: "0.75rem",
              fontWeight: 500,
              borderRadius: "2px",
              backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
              border: `1px solid ${borderColor}`,
              "& .MuiSelect-select": { py: 0.5 },
              "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)" },
            }}
          >
            {Object.entries(TIME_WINDOWS).map(([key, { label }]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Feed list — consistent spacing, hover states */}
      <Box
        component="ul"
        role="list"
        sx={{
          flex: 1,
          overflowY: "auto",
          padding: "6px 0",
          margin: 0,
          listStyle: "none",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: isDark ? "rgba(148, 163, 184, 0.25)" : "rgba(0, 0, 0, 0.12)",
            borderRadius: 2,
          },
        }}
      >
        {displayList.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 100,
              padding: 1.5,
              color: textSecondary,
              fontSize: "0.8125rem",
              fontWeight: 500,
            }}
          >
            No notifications in this period
          </Box>
        ) : (
          displayList.map((n, index) => {
            const color = getEventColor(n.event, n.severity);
            return (
              <Box
                component="li"
                key={n.id}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.25,
                  padding: "8px 14px",
                  margin: "0 6px",
                  borderRadius: RADIUS.block,
                  transition: "background 0.2s ease",
                  "&:hover": {
                    backgroundColor: isDark ? "rgba(255, 255, 255, 0.04)" : "rgba(0, 0, 0, 0.03)",
                  },
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: RADIUS.icon,
                    backgroundColor: `${color}18`,
                    border: `1px solid ${color}35`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color,
                    flexShrink: 0,
                  }}
                >
                  {getEventIcon(n.event)}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        color: textPrimary,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {n.tag}
                    </Typography>
                    <Typography sx={{ fontSize: "0.625rem", color: textSecondary, flexShrink: 0, fontWeight: 500 }}>
                      {formatTime(n.timestamp)}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: "0.6875rem", color, fontWeight: 600 }}>
                    {n.event}
                  </Typography>
                  {n.facility && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
                      <LocationOnIcon sx={{ fontSize: "0.6875rem", color: textSecondary }} />
                      <Typography sx={{ fontSize: "0.625rem", color: textSecondary }}>{n.facility}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })
        )}
      </Box>
    </Paper>
  );
});

export default NotificationsFeed;
